import isNil from 'lodash/isNil';

import { Project, TestCycleProject } from '@qality/common-model';
import leftArrow from '@qality/common-static/images/LeftArrow.svg';
import rightArrow from '@qality/common-static/images/RightArrow.svg';
import ArrowWithShadowIcon from '@qality/common-static/images/arrow-with-shadow.svg';
import fileUploadIcon from '@qality/common-static/images/file-upload-icon.svg';
import qalityIssueIcon from '@qality/common-static/images/qality-issue-icon.png';
import qualityPlusImage from '@qality/common-static/images/qality-plus.svg';
import traceabilityReportPageCover from '@qality/common-static/images/traceabilityReportPageCover.svg';

import HostNameService from './HostNameService';
import WindowOriginService from './WindowOriginService';

const ADDON_KEY = plugin.addonKey;

export default {
  getIssuesUrl(params: { filter: string; jql: string }): string {
    const hostName = HostNameService.baseUrl;
    let issuesURL = `${hostName}/issues/?`;

    if (params?.jql) {
      issuesURL += `jql=${encodeURIComponent(params.jql)}&`;
    }

    if (params?.filter) {
      issuesURL += `filter=${encodeURIComponent(params.filter)}&`;
    }

    return issuesURL;
  },
  async getEmptyTestCaseExecutionUrl(): Promise<string> {
    await HostNameService.init();
    const hostName = HostNameService.baseUrl;
    return `${hostName}/plugins/servlet/ac/${ADDON_KEY}/test-case-execution?testExecutionId=`;
  },

  getTestCycleUrl(testCycleId: number, numberOfAddedTestCases: number): string {
    const hostName = HostNameService.baseUrl;
    let url = `${hostName}/plugins/servlet/ac/${ADDON_KEY}/test-cycle-details?testCycleId=${testCycleId}`;

    if (numberOfAddedTestCases) {
      url += `&addedTC=${numberOfAddedTestCases}`;
    }

    return url;
  },

  getNextGenIssueTypeConfigUrl(projectKey: string): string {
    const hostName = HostNameService.baseUrl;
    return `${hostName}/jira/software/projects/${projectKey}/settings/issuetypes`;
  },

  getProjectUrl(projectKey: string): string {
    const hostName = HostNameService.baseUrl;
    return `${hostName}/browse/${projectKey}`;
  },

  getVersionUrl(projectKey: string, versionId: string): string {
    const hostName = HostNameService.baseUrl;
    return `${hostName}/projects/${projectKey}/versions/${versionId}`;
  },

  getTestCaseExecutionUrl(testExecutionId: number): string {
    const hostName = HostNameService.baseUrl;
    return `${hostName}/plugins/servlet/ac/${ADDON_KEY}/test-case-execution?testExecutionId=${testExecutionId}`;
  },

  getTestCaseExecutionHistoryUrl(
    testCaseKey: string,
    issueKey: string,
    pageNumber = 0
  ): string {
    const hostName = HostNameService.baseUrl;
    return (
      `${hostName}/plugins/servlet/ac/${ADDON_KEY}` +
      `/test-case-execution-history#!testCaseKey=${testCaseKey}&issueKey=${issueKey}&pageNumber=${pageNumber}`
    );
  },

  getTestCyclesUrl(projectKey: string, projectId: string): string {
    const hostName = HostNameService.baseUrl;
    return (
      `${hostName}/plugins/servlet/ac/${ADDON_KEY}` +
      `/test-cycles?projectKey=${projectKey}&project.id=${projectId}`
    );
  },

  getProjectDashboardUrl(projectKey: string, projectId: string): string {
    const hostName = HostNameService.baseUrl;
    return `${hostName}/plugins/servlet/ac/${ADDON_KEY}/welcome-page?project.key=${projectKey}&project.id=${projectId}`;
  },

  getGlobalDashboardUrl(): string {
    const hostName = HostNameService.baseUrl;
    return `${hostName}/plugins/servlet/ac/${ADDON_KEY}/welcome`;
  },

  async getTestCasesOverviewLink(): Promise<string> {
    await HostNameService.init();
    const hostName = HostNameService.baseUrl;
    return `${hostName}/issues/?jql=issuetype %3D 'QAlity Test' order by created DESC`;
  },

  getTestCasesImportLink(projectKey: string): string {
    const hostName = HostNameService.baseUrl;
    if (projectKey) {
      return `${hostName}/projects/${projectKey}?selectedItem=${ADDON_KEY}__qality-project-import-test-cases-panel&projectKey=${projectKey}`;
    }
    return `${hostName}/plugins/servlet/ac/${ADDON_KEY}/import-test-cases`;
  },

  getUrlToProjectIcon(project: Project): string {
    return project.avatarUrls['24x24'];
  },

  getAvatarUrl(img: string): string {
    const hostName = HostNameService.baseUrl;
    return hostName + img;
  },

  async getBrowseIssueUrl(issueKey: string): Promise<string> {
    await HostNameService.init();
    await WindowOriginService.init();
    const hostName = HostNameService.baseUrl;
    return hostName + '/browse/' + issueKey;
  },

  getProjectConfigIssueTypesUrl(project: Project): string {
    const hostName = HostNameService.baseUrl;
    return project.style === 'next-gen'
      ? hostName + `/projects/${project.key}/settings/issuetypes/`
      : hostName + `/plugins/servlet/project-config/${project.key}/issuetypes`;
  },

  async getURLSearchParams(): Promise<URLSearchParams> {
    // eslint-disable-next-line promise/avoid-new
    return new Promise((resolve) => {
      window.AP.getLocation((locationHref) => {
        let hash = new URL(locationHref).hash;
        hash = hash.replace('#!', '?');
        resolve(new URLSearchParams(hash));
      });
    });
  },

  getAllProjectsURL(): string {
    const hostName = HostNameService.baseUrl;
    return `${hostName}/secure/BrowseProjects.jspa`;
  },

  getProjectURL(projectKey: string): string {
    const hostName = HostNameService.baseUrl;
    return `${hostName}/browse/${projectKey}`;
  },

  getReportsURL(projectKey: string): string {
    const hostName = HostNameService.baseUrl;
    return `${hostName}/projects/${projectKey}?selectedItem=com.atlassian.jira.jira-projects-plugin:report-page`;
  },

  getIssueURL(issueKey: string): string {
    const hostName = HostNameService.baseUrl;
    return hostName + '/browse/' + issueKey;
  },

  setUrlParams(params: Array<{ name: string; value: string }>): void {
    const searchParams = new URLSearchParams();
    params.forEach(function (param) {
      searchParams.set(param.name, param.value);
    });
    window.AP.history.replaceState(searchParams.toString());
  },

  updateUrlParams(
    params: Array<{ omit: boolean; name: string; value: string }>
  ): Promise<void> {
    return new Promise((resolve) => {
      window.AP.getLocation(function (location) {
        let hash = new URL(location).hash;
        hash = hash.replace('#!', '?');
        const searchParams = new URLSearchParams(hash);
        params.forEach(function (param) {
          if (param.omit) {
            searchParams.delete(param.name);
          } else {
            searchParams.set(param.name, param.value);
          }
        });
        window.AP.history.replaceState(searchParams.toString());
        resolve();
      });
    });
  },

  getQAlityIssueIcon(): string {
    return qalityIssueIcon;
  },

  getQAlityPlusImage(): string {
    return qualityPlusImage;
  },

  getFileUploadIconPath(): string {
    return fileUploadIcon;
  },

  getTraceabilityReportPageCoverPath(): string {
    return traceabilityReportPageCover;
  },

  getLeftArrowIconPath(): string {
    return leftArrow;
  },

  getRightArrowIconPath(): string {
    return rightArrow;
  },

  getArrowWithShadowIcon(): string {
    return ArrowWithShadowIcon;
  },

  getUrlToSearchIssues(keys: Array<string>): string {
    return (
      HostNameService.baseUrl + '/issues/?jql=%20key%20in(' + keys.join() + ')'
    );
  },

  async getCompleteUrlToBulkAddTestCases(): Promise<string> {
    let url = this.getBulkAddTestCasesUrl();

    const urlSearchParams = await this.getJiraURLSearchParams();

    const jql = urlSearchParams.get('jql');
    const filterId = urlSearchParams.get('filter');

    if (!isNil(jql)) {
      url += `jql=${jql}`;
    } else if (!isNil(filterId)) {
      url += `filterId=${filterId}`;
    }

    return url;
  },

  getBulkAddTestCasesUrl(): string {
    const hostName = HostNameService.baseUrl;
    return `${hostName}/plugins/servlet/ac/${ADDON_KEY}/add-test-cases?`;
  },

  async getJiraURLSearchParams(): Promise<URLSearchParams> {
    // eslint-disable-next-line promise/avoid-new
    return new Promise((resolve) => {
      window.AP.getLocation((locationHref) => {
        const search = new URL(locationHref).search;
        resolve(new URLSearchParams(search));
      });
    });
  },

  getSettingsUrl(): string {
    const hostName = HostNameService.baseUrl;
    return `${hostName}/plugins/servlet/ac/${ADDON_KEY}/settings-general-page`;
  },

  getQalityPlusMarketplaceUrl(): string {
    const hostName = HostNameService.baseUrl;
    return `${hostName}/jira/marketplace/discover/app/com.soldevelo.apps.test_management_premium`;
  },

  getQalityVersionsComparisonUrl(): string {
    return `https://soldevelo.atlassian.net/l/c/cGDekdrn`;
  },

  buildUrlWithParams: (urlBase: string, urlParams: Array<string>): string => {
    return [urlBase, urlParams.join('&')].join('?');
  },

  getManageAppUrl(): string {
    const hostName = HostNameService.baseUrl;
    return `${hostName}/plugins/servlet/upm`;
  },

  getStatusDocumentationUrl(): string {
    return 'https://soldevelo.atlassian.net/wiki/spaces/QS/pages/4581425174/Custom+Execution+Statuses';
  },
  getTestExecutionReportUrl(
    testCycleId: string,
    testCycleProject: TestCycleProject
  ): string {
    const hostName = HostNameService.baseUrl;
    const args = `&project.key=${testCycleProject.key}&project.id=${testCycleProject.id}#!startDate=none&endDate=none&projectId=ALL_PROJECTS&testCyclesIds=${testCycleId}`;
    return `${hostName}/plugins/servlet/ac/${ADDON_KEY}/test-execution-report?reportName=TestExecutionReport${args}`;
  },
};
